import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Phone, PhoneMissed, Facebook, Linkedin, Globe, 
  CheckCircle, XCircle, AlertCircle, RefreshCw, 
  Copy, ExternalLink, Zap, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsPage({ user, logout }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [integrations, setIntegrations] = useState({
    ivr: { status: 'checking', lastLead: null, count: 0 },
    missed_call: { status: 'checking', lastLead: null, count: 0 },
    facebook: { status: 'checking', lastLead: null, count: 0, error: null },
    linkedin: { status: 'checking', lastLead: null, count: 0 },
    google_ads: { status: 'checking', lastLead: null, count: 0 },
    website: { status: 'checking', lastLead: null, count: 0 }
  });
  const [fbToken, setFbToken] = useState('');
  const [savingFbToken, setSavingFbToken] = useState(false);

  const WEBHOOK_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks`;

  const webhookUrls = {
    ivr: `${WEBHOOK_BASE}/ivr`,
    missed_call: `${WEBHOOK_BASE}/missed-call`,
    facebook: `${WEBHOOK_BASE}/facebook-leads`,
    linkedin: `${WEBHOOK_BASE}/linkedin-leads`,
    google_ads: `${WEBHOOK_BASE}/google-leads`,
    website: `${WEBHOOK_BASE}/lead-capture`
  };

  useEffect(() => {
    checkAllIntegrations();
  }, []);

  const checkAllIntegrations = async () => {
    setLoading(true);
    try {
      // Check lead counts by source
      const sources = ['ivr', 'missed_call', 'facebook', 'linkedin', 'google', 'website'];
      const newIntegrations = { ...integrations };

      for (const source of sources) {
        try {
          const response = await axios.get(`${API}/api/leads?source=${source}&limit=1&sort_field=created_at&sort_order=desc`, 
            { withCredentials: true });
          const key = source === 'google' ? 'google_ads' : source;
          newIntegrations[key] = {
            status: response.data.total > 0 ? 'active' : 'no_leads',
            lastLead: response.data.leads?.[0] || null,
            count: response.data.total || 0
          };
        } catch (err) {
          console.error(`Error checking ${source}:`, err);
        }
      }

      // Check Facebook token status
      try {
        const fbResponse = await axios.get(`${API}/api/webhooks/facebook/pages`, { withCredentials: true });
        if (fbResponse.data && !fbResponse.data.detail?.includes('error')) {
          newIntegrations.facebook.status = 'connected';
        }
      } catch (err) {
        const errorMsg = err.response?.data?.detail || '';
        if (errorMsg.includes('expired') || errorMsg.includes('Session')) {
          newIntegrations.facebook.status = 'token_expired';
          newIntegrations.facebook.error = 'Access token has expired';
        } else {
          newIntegrations.facebook.status = 'not_configured';
          newIntegrations.facebook.error = 'Not configured';
        }
      }

      // Check LinkedIn status
      try {
        const liResponse = await axios.get(`${API}/api/webhooks/linkedin-status`, { withCredentials: true });
        if (liResponse.data?.connected) {
          newIntegrations.linkedin.status = 'connected';
        } else {
          newIntegrations.linkedin.status = 'not_connected';
        }
      } catch (err) {
        newIntegrations.linkedin.status = 'not_connected';
      }

      setIntegrations(newIntegrations);
    } catch (error) {
      toast.error('Failed to check integrations');
    } finally {
      setLoading(false);
    }
  };

  const refreshIntegration = async (source) => {
    setRefreshing(true);
    await checkAllIntegrations();
    setRefreshing(false);
    toast.success('Integration status refreshed');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const testWebhook = async (source) => {
    try {
      const testData = {
        ivr: { SourceNumber: '9999888877', CustomerName: 'TEST_IVR_Lead', key_press: '1,2', Status: 'completed', CallDuration: '30' },
        missed_call: { SourceNumber: '9999888878', CustomerName: 'TEST_Missed_Call_Lead', Status: 'missed' },
        website: { name: 'TEST_Website_Lead', phone: '9999888879', source: 'website' },
        google_ads: { full_name: 'TEST_Google_Lead', phone_number: '9999888876', campaign_name: 'Test Campaign' }
      };

      const endpointMap = {
        website: 'lead-capture',
        missed_call: 'missed-call',
        ivr: 'ivr',
        google_ads: 'google-leads'
      };
      
      const endpoint = endpointMap[source] || source;
      const response = await axios.post(`${API}/api/webhooks/${endpoint}`, testData[source] || {});
      
      if (response.data.status === 'ok' || response.data.success) {
        toast.success(`Test lead created successfully! Check leads page for TEST_${source.toUpperCase()}_Lead`);
        checkAllIntegrations();
      } else {
        toast.error('Test failed');
      }
    } catch (error) {
      toast.error('Failed to test webhook');
    }
  };

  const saveFacebookToken = async () => {
    if (!fbToken.trim()) {
      toast.error('Please enter a token');
      return;
    }
    setSavingFbToken(true);
    try {
      await axios.post(`${API}/api/webhooks/facebook/set-token`, { token: fbToken }, { withCredentials: true });
      toast.success('Facebook token saved');
      setFbToken('');
      checkAllIntegrations();
    } catch (error) {
      toast.error('Failed to save token');
    } finally {
      setSavingFbToken(false);
    }
  };

  const initiateLinkedInAuth = async () => {
    try {
      const response = await axios.get(`${API}/api/webhooks/linkedin-auth`, { withCredentials: true });
      if (response.data.auth_url) {
        window.open(response.data.auth_url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to start LinkedIn authorization');
    }
  };

  const getStatusBadge = (integration) => {
    const { status, count } = integration;
    
    if (status === 'checking') {
      return <Badge variant="outline" className="text-slate-500"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Checking...</Badge>;
    }
    if (status === 'connected' || status === 'active') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Active ({count} leads)</Badge>;
    }
    if (status === 'token_expired') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Token Expired</Badge>;
    }
    if (status === 'not_connected' || status === 'not_configured') {
      return <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="w-3 h-3 mr-1" /> Not Connected</Badge>;
    }
    if (status === 'no_leads') {
      return <Badge variant="outline" className="text-slate-500"><AlertCircle className="w-3 h-3 mr-1" /> No leads yet</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const IntegrationCard = ({ title, icon: Icon, source, description, webhookUrl, canTest }) => (
    <Card className="relative">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
              <Icon className="w-5 h-5 text-cyan-700" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          {getStatusBadge(integrations[source])}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Webhook URL */}
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Webhook URL</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded truncate">{webhookUrl}</code>
            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookUrl)}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Last Lead */}
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Last Lead Received</p>
          {integrations[source].lastLead ? (
            <div className="text-sm">
              <p className="font-medium">{integrations[source].lastLead.name}</p>
              <p className="text-xs text-slate-500">{formatDate(integrations[source].lastLead.created_at)}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No leads received yet</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {canTest && (
            <Button size="sm" variant="outline" onClick={() => testWebhook(source)}>
              <Zap className="w-4 h-4 mr-1" /> Test
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => refreshIntegration(source)}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="p-6 space-y-6" data-testid="integrations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Lead Integrations</h1>
            <p className="text-slate-500">Manage and monitor all lead capture integrations</p>
          </div>
          <Button onClick={checkAllIntegrations} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh All
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <p className="text-sm text-blue-600">IVR Leads</p>
              <p className="text-2xl font-bold text-blue-700">{integrations.ivr.count}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-4">
              <p className="text-sm text-orange-600">Missed Call</p>
              <p className="text-2xl font-bold text-orange-700">{integrations.missed_call.count}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100">
            <CardContent className="p-4">
              <p className="text-sm text-indigo-600">Facebook</p>
              <p className="text-2xl font-bold text-indigo-700">{integrations.facebook.count}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100">
            <CardContent className="p-4">
              <p className="text-sm text-cyan-600">LinkedIn</p>
              <p className="text-2xl font-bold text-cyan-700">{integrations.linkedin.count}</p>
            </CardContent>
          </Card>
        </div>

        {/* Integration Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* IVR */}
          <IntegrationCard
            title="IVR (CallerDesk)"
            icon={Phone}
            source="ivr"
            description="Receive leads from IVR calls on 9888438843"
            webhookUrl={webhookUrls.ivr}
            canTest={true}
          />

          {/* Missed Call */}
          <IntegrationCard
            title="Missed Call"
            icon={PhoneMissed}
            source="missed_call"
            description="Capture leads from missed calls on 9654940400"
            webhookUrl={webhookUrls.missed_call}
            canTest={true}
          />

          {/* Facebook */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Facebook className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Facebook Lead Ads</CardTitle>
                    <CardDescription className="text-xs">Sync leads from Facebook campaigns</CardDescription>
                  </div>
                </div>
                {getStatusBadge(integrations.facebook)}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded truncate">{webhookUrls.facebook}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookUrls.facebook)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {integrations.facebook.status === 'token_expired' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium">Token Expired!</p>
                  <p className="text-xs text-red-600 mt-1">Please enter a new Page Access Token from Facebook Business Settings</p>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Paste new access token..."
                      value={fbToken}
                      onChange={(e) => setFbToken(e.target.value)}
                      className="text-xs"
                    />
                    <Button size="sm" onClick={saveFacebookToken} disabled={savingFbToken}>
                      {savingFbToken ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Last Lead</p>
                {integrations.facebook.lastLead ? (
                  <p className="text-sm">{integrations.facebook.lastLead.name} - {formatDate(integrations.facebook.lastLead.created_at)}</p>
                ) : (
                  <p className="text-sm text-slate-400">No leads yet</p>
                )}
              </div>

              <Button size="sm" variant="outline" onClick={() => window.open('https://business.facebook.com/settings/system-users', '_blank')}>
                <ExternalLink className="w-4 h-4 mr-1" /> Facebook Business Settings
              </Button>
            </CardContent>
          </Card>

          {/* LinkedIn */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Linkedin className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">LinkedIn Lead Gen</CardTitle>
                    <CardDescription className="text-xs">Sync leads from LinkedIn campaigns</CardDescription>
                  </div>
                </div>
                {getStatusBadge(integrations.linkedin)}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded truncate">{webhookUrls.linkedin}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookUrls.linkedin)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {integrations.linkedin.status === 'not_connected' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700 font-medium">Not Connected</p>
                  <p className="text-xs text-yellow-600 mt-1">Click below to authorize LinkedIn access</p>
                  <Button size="sm" className="mt-2" onClick={initiateLinkedInAuth}>
                    <Linkedin className="w-4 h-4 mr-1" /> Connect LinkedIn
                  </Button>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Last Lead</p>
                {integrations.linkedin.lastLead ? (
                  <p className="text-sm">{integrations.linkedin.lastLead.name} - {formatDate(integrations.linkedin.lastLead.created_at)}</p>
                ) : (
                  <p className="text-sm text-slate-400">No leads yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Website */}
          <IntegrationCard
            title="Website Lead Form"
            icon={Globe}
            source="website"
            description="Capture leads from your website forms"
            webhookUrl={webhookUrls.website}
            canTest={true}
          />

          {/* Google Ads */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Google Ads</CardTitle>
                    <CardDescription className="text-xs">Capture leads from Google Lead Form extensions</CardDescription>
                  </div>
                </div>
                {getStatusBadge(integrations.google_ads)}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded truncate">{webhookUrls.google_ads}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(webhookUrls.google_ads)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-600">
                  Configure this webhook URL in your Google Ads account under Lead Form Extensions {">"} Webhook delivery.
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Last Lead</p>
                {integrations.google_ads.lastLead ? (
                  <p className="text-sm">{integrations.google_ads.lastLead.name} - {formatDate(integrations.google_ads.lastLead.created_at)}</p>
                ) : (
                  <p className="text-sm text-slate-400">No leads yet</p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => testWebhook('google_ads')}>
                  <Zap className="w-4 h-4 mr-1" /> Test
                </Button>
                <Button size="sm" variant="ghost" onClick={() => refreshIntegration('google_ads')}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Setup Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Instructions</CardTitle>
            <CardDescription>Step-by-step guides to connect each integration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* CallerDesk Setup */}
              <div className="border rounded-lg p-4 border-blue-200 bg-blue-50/30">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> CallerDesk (IVR & Missed Call)
                </h4>
                <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                  <li>Login to <a href="https://callerdesk.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">CallerDesk dashboard</a></li>
                  <li>Go to <strong>Settings {">"} Webhooks / API Integration</strong></li>
                  <li>For IVR (9888438843):
                    <div className="mt-1 ml-4">
                      <code className="text-xs bg-white px-2 py-1 rounded border block">{webhookUrls.ivr}</code>
                    </div>
                  </li>
                  <li>For Missed Call (9654940400):
                    <div className="mt-1 ml-4">
                      <code className="text-xs bg-white px-2 py-1 rounded border block">{webhookUrls.missed_call}</code>
                    </div>
                  </li>
                  <li>Set <strong>HTTP Method: POST</strong>, <strong>Content-Type: JSON</strong></li>
                  <li>Enable webhook and save</li>
                </ol>
                <p className="text-xs text-blue-700 mt-3 bg-blue-100 p-2 rounded">
                  💡 Use the <strong>Test</strong> button above to verify the integration works
                </p>
              </div>

              {/* Facebook Setup */}
              <div className="border rounded-lg p-4 border-indigo-200 bg-indigo-50/30">
                <h4 className="font-medium text-indigo-900 mb-2 flex items-center gap-2">
                  <Facebook className="w-4 h-4" /> Facebook Lead Ads
                </h4>
                <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                  <li>Go to <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Facebook Business Settings {">"} System Users</a></li>
                  <li>Create or select a System User</li>
                  <li>Click <strong>Generate Token</strong></li>
                  <li>Select your Page and enable <strong>ads_management</strong> & <strong>leads_retrieval</strong></li>
                  <li>Copy the generated token</li>
                  <li>Paste it in the Facebook card above</li>
                </ol>
                <p className="text-xs text-indigo-700 mt-3 bg-indigo-100 p-2 rounded">
                  💡 Tokens expire after 60 days. Regenerate when you see "Token Expired" error
                </p>
              </div>

              {/* Google Ads Setup */}
              <div className="border rounded-lg p-4 border-red-200 bg-red-50/30">
                <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Google Ads Lead Forms
                </h4>
                <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                  <li>Login to <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads</a></li>
                  <li>Go to <strong>Assets {">"} Lead Form Extensions</strong></li>
                  <li>Edit your Lead Form</li>
                  <li>Under <strong>Lead Delivery</strong>, select <strong>Webhook</strong></li>
                  <li>Paste webhook URL:
                    <div className="mt-1 ml-4">
                      <code className="text-xs bg-white px-2 py-1 rounded border block">{webhookUrls.google_ads}</code>
                    </div>
                  </li>
                  <li>Save the Lead Form</li>
                </ol>
              </div>

              {/* LinkedIn Setup */}
              <div className="border rounded-lg p-4 border-sky-200 bg-sky-50/30">
                <h4 className="font-medium text-sky-900 mb-2 flex items-center gap-2">
                  <Linkedin className="w-4 h-4" /> LinkedIn Lead Gen
                </h4>
                <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                  <li>Click <strong>Connect LinkedIn</strong> button above</li>
                  <li>Authorize HearClear CRM to access your LinkedIn</li>
                  <li>Select the ad accounts to sync</li>
                  <li>Leads will auto-sync once connected</li>
                </ol>
                <p className="text-xs text-sky-700 mt-3 bg-sky-100 p-2 rounded">
                  💡 You must have admin access to LinkedIn Campaign Manager
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-amber-800">Troubleshooting</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-amber-900">IVR/Missed Call not working?</p>
                <ul className="list-disc list-inside text-slate-600 mt-1 space-y-1">
                  <li>Verify webhook URL is correct in CallerDesk</li>
                  <li>Use the "Test" button to verify CRM receives data</li>
                  <li>Check if CallerDesk is sending requests (ask their support)</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-amber-900">Facebook token expired?</p>
                <ul className="list-disc list-inside text-slate-600 mt-1 space-y-1">
                  <li>Generate a new token from Business Settings</li>
                  <li>Use "Never Expire" option if available</li>
                  <li>Save new token in the Facebook card above</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
